/* eslint-disable no-console */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ENV, IEnvironment } from 'env/config';

// Documentation: https://www.npmjs.com/package/http-status-codes
import { StatusCodes } from 'http-status-codes';
import { commonConstants } from '../constants/common.constants';

@Injectable()
export class RoleInterceptorService implements HttpInterceptor {
  logoutCompleted = false;
  refreshTokenInProgress = false;
  tokenRefreshedSource = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  private readonly centralApis = [];

  constructor(
    @Inject(ENV) private env: IEnvironment,
    private authService: AuthService,
    private router: Router,
    private translationService: TranslocoService
  ) {
    this.centralApis = Object.values(this.env.api.backend.central.endpoint);
  }

  manage401(error: HttpErrorResponse) {
    return this.refreshToken().pipe(
      catchError((e) => {
        console.debug('manage401 error', e);
        if (e.status === 401) {
          return this.logout(e);
        }
        return e;
      })
    );
  }

  manage403(error: HttpErrorResponse) {
    console.debug('manage403', error);

    const url = this.router.routerState.snapshot.url;
    if (url === '/login') {
      return this.logout(error);
    } else {
      sessionStorage.setItem(
        'restrictedPage',
        JSON.stringify(this.router.routerState.snapshot.url)
      );
      const promiseNavigation = this.router.navigateByUrl('/app/menu');

      return from(promiseNavigation).pipe(
        concatMap((response) => of(new HttpResponse()))
      );
    }
  }

  manageDefaultError(error: any) {
    if (this.logoutCompleted) {
      return of(new HttpResponse({}));
    }

    return throwError(error);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // All HTTP requests are going to go through this method
    let newRequest = req;

    if (this.checkCentralApi(req.url)) {
      newRequest = req.clone({
        headers: req.headers.set('env', this.env.api.headers.central.env),
      });
    }

    return next.handle(newRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case StatusCodes.UNAUTHORIZED: //401
            const isStillValidSession = sessionStorage.getItem('sso') !== null;
            if (!isStillValidSession) {
              return of(new HttpResponse({}));
            }

            if (error?.error?.message === commonConstants.INACTIVE_USER) {
              return this.logout(error);
            }

            return this.manage401(error).pipe(
              switchMap(() => {
                newRequest = this.addAuthHeader(newRequest);

                if (!this.logoutCompleted) {
                  console.debug(
                    'RoleInterceptor: token updated and retrying',
                    this.logoutCompleted
                  );

                  return next.handle(newRequest);
                } else {
                  return of(new HttpResponse({})).pipe(
                    tap(() => (this.logoutCompleted = false))
                  );
                }
              })
            );
          case StatusCodes.FORBIDDEN: //403
            return this.manage403(error);
          default:
            return this.manageDefaultError(error);
        }
      })
    );
  }

  private checkCentralApi(url: string): boolean {
    let isCentralApi = false;

    for (const item of this.centralApis) {
      if (url.startsWith(item)) {
        isCentralApi = true;
        break;
      }
    }

    return isCentralApi;
  }

  private logout(error: any) {
    const err = error?.error?.message ?? error.statusText ?? 'Unauthorized';
    const logoutPromise = this.authService.navigateToLoginPage(true, err);
    return from(logoutPromise).pipe(
      concatMap((response) => of(new HttpResponse())),
      tap(() => (this.logoutCompleted = true))
    );
  }

  private addAuthHeader(request: HttpRequest<any>) {
    const authHeader = this.authService.oauthService.authorizationHeader();
    if (authHeader) {
      return request.clone({
        setHeaders: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Authorization: authHeader,
        },
      });
    }
    return request;
  }

  private refreshToken(): Observable<any> {
    if (this.refreshTokenInProgress) {
      return new Observable((observer) => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(undefined);
          observer.complete();
        });
      });
    } else {
      this.refreshTokenInProgress = true;

      return this.authService.refreshToken().pipe(
        tap(() => {
          this.refreshTokenInProgress = false;
          this.tokenRefreshedSource.next(true);
        }),
        catchError((error) => {
          this.refreshTokenInProgress = false;

          const err = this.translationService.translate(
            `errors.sessionExpired`
          );
          error.error.message = err;
          return this.logout(error);
        })
      );
    }
  }
}
